import { TranslateService } from "@ngx-translate/core";
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import * as moment from "moment";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-report-export-pdf",
  templateUrl: "./report-export-pdf.component.html",
  styleUrls: ["./report-export-pdf.component.scss"],
})
export class ReportExportPdfComponent implements OnInit {
  @ViewChild("pdf") pdf!: ElementRef;

  @Input() patientInfo: any;
  @Input() pdfModel: any;
  @Output() created = new EventEmitter<any>();

  serverPath: string = environment.serverPath;
  showCarePlan: boolean = false;

  constructor(private translationService: TranslateService) {}

  ngOnInit(): void {
    this.initData();
  }

  initData() {
    if (!this.pdfModel) {
      setTimeout(() => {
        this.initData();
      }, 10);
      return;
    }
    if (this.pdfModel.procedures && this.pdfModel.procedures.length > 0) {
      for (const item of this.pdfModel.procedures) {
        item.categoryLabel = item.category?.text;
        item.codeLabel = item.code?.text;
        item.dateLabel = moment(item.performedDateTime, "YYYY-MM-DD").format("DD/MM/YYYY");
        item.reasonLabel = item.reasonCode?.[0]?.text ? item.reasonCode[0].text : null;
        item.noteLabel = item.note?.[0]?.text ? item.note[0].text : null;
      }
    }
    if (this.pdfModel.medication && this.pdfModel.medication.length > 0) {
      for (const item of this.pdfModel.medication) {
        item.medicationLabel = item.medicationCodeableConcept.text;
        item.periodLabel = `${moment(item.dosageInstruction[0].timing.repeat.boundsPeriod.start, "YYYY-MM-DD").format(
          "DD/MM/YYYY"
        )} - ${
          item.dosageInstruction[0].timing.repeat.boundsPeriod.end
            ? moment(item.dosageInstruction[0].timing.repeat.boundsPeriod.end, "YYYY-MM-DD").format("DD/MM/YYYY")
            : this.translationService.instant("exportPdfPage.ongoing")
        }`;
      }
    }
    if (this.pdfModel.carePlan && this.pdfModel.carePlan.length > 0) {
      let hasAnyVisibleCategories = false;
      for (const type of this.pdfModel.carePlan) {
        let hasVisibleItems = false;
        if (type.values && type.values.length > 0) {
          type.categoryLabel = this.translationService.instant("careplan.headers." + type.type);
          for (const item of type.values) {
            item.show = false;
            item.isInactive = false;
            const isFinishedStatus = this.isInactiveStatus(item.Status);
            const isExpired = this.hasEndDateExpired(item.EndDate);
            const allowInactive = item.ResourceType !== "Questionnaire" && (isFinishedStatus || isExpired);
            let isActive = false;
            switch (item.ResourceType) {
              case "Observation":
                isActive = !isFinishedStatus && !isExpired;
                if (isActive || allowInactive) {
                  item.show = true;
                  item.label = this.translationService.instant("medicalRecord.observationCode." + item.ObservationName);
                  item.rangeLabel = `${moment(item.StartDate).format("DD/MM/YYYY")} - ${moment(item.EndDate).format(
                    "DD/MM/YYYY"
                  )}`;
                  item.frequencyLabel = item.Frequency;
                  item.periodUnitLabel = this.translationService.instant("activityItem.periodUnits." + item.PeriodUnit);
                  if (item.TimeOfDay && item.TimeOfDay.length > 0) {
                    item.timeOfDayLabel = item.TimeOfDay.join(", ");
                  }
                }
                break;
              case "MedicationRequest":
                isActive = !isFinishedStatus && !isExpired;
                if (isActive || allowInactive) {
                  const daysOfWeek: any[] = [];
                  if (item.DaysOfWeek && item.DaysOfWeek.length > 0) {
                    item.DaysOfWeek.forEach((z: string) => {
                      daysOfWeek.push(this.translationService.instant("exportPdfPage." + z));
                    });
                  }
                  item.show = true;
                  item.label = item.DrugName;
                  item.rangeLabel = `${moment(item.StartDate).format("DD/MM/YYYY")} - ${moment(item.EndDate).format(
                    "DD/MM/YYYY"
                  )}`;
                  item.frequencyLabel = item.Frequency;
                  item.periodUnitLabel = this.translationService.instant("activityItem.periodUnits." + item.PeriodUnit);
                  item.doseLabel = `${item.DoseValue} ${item.DoseUnit}`;
                  item.daysOfWeekLabel = daysOfWeek.length > 0 ? daysOfWeek.join(", ") : null;
                  if (item.TimeOfDay && item.TimeOfDay.length > 0) item.timeOfDayLabel = item.TimeOfDay.join(", ");
                }
                break;
              case "NutritionOrder":
                isActive = !isFinishedStatus && !isExpired;
                if (isActive || allowInactive) {
                  item.show = true;
                  item.label = item.Nutrition?.plan?.nutritionPlan?.Name;
                  item.rangeLabel = `${moment(item.StartDate).format("DD/MM/YYYY")} - ${moment(item.EndDate).format(
                    "DD/MM/YYYY"
                  )}`;
                }
                break;
              case "Education":
                isActive = !isFinishedStatus && !isExpired;
                if (isActive || allowInactive) {
                  item.show = true;
                  item.label = item.EducationalMaterial?.Name;
                  item.rangeLabel = `${moment(item.StartDate).format("DD/MM/YYYY")} - ${moment(item.EndDate).format(
                    "DD/MM/YYYY"
                  )}`;
                  if (
                    item.EducationalMaterial?.EducationType !== null &&
                    item.EducationalMaterial?.EducationType !== undefined
                  ) {
                    item.typeLabel = `${this.translationService.instant(
                      "educationTypes." + item.EducationalMaterial?.EducationType
                    )}`;
                  }
                  item.PathLabel = item.EducationalMaterial?.Path;
                }
                break;
              case "Laboratory":
                isActive = !isFinishedStatus && !isExpired;
                if (isActive || allowInactive) {
                  item.show = true;
                  item.label = item.LaboratoryTestName;
                  item.rangeLabel = `${moment(item.StartDate).format("DD/MM/YYYY")}`;
                }
                break;
              case "Exercise":
                isActive = !isFinishedStatus && !isExpired;
                if (isActive || allowInactive) {
                  item.show = true;
                  item.label = item.ExerciseType;
                  item.rangeLabel = `${moment(item.StartDate).format("DD/MM/YYYY")} - ${moment(item.EndDate).format(
                    "DD/MM/YYYY"
                  )}`;
                  item.frequencyLabel = item.Frequency;
                  item.periodUnitLabel = this.translationService.instant("activityItem.periodUnits." + item.PeriodUnit);
                  item.periodValue = item.Period;
                  if (item.DayOfWeek && item.DayOfWeek.length > 0) {
                    item.daysOfWeekLabel = item.DayOfWeek.map((day: string) =>
                      this.translationService.instant("exportPdfPage." + day)
                    ).join(", ");
                  }
                  if (item.Count !== null && item.Count !== undefined) {
                    item.countLabel = item.Count;
                  }
                  if (item.TimeOfDay && item.TimeOfDay.length > 0) {
                    item.timeOfDayLabel = item.TimeOfDay.join(", ");
                  }
                }
                break;
              case "Questionnaire":
                if (item.answers && item.answers.length > 0) {
                  item.answers.forEach((answer: any) => {
                    this.getQuestionnaireScore(answer);
                  });
                }
                isActive = item.Status !== "finished";
                if (isActive) {
                  item.show = true;
                  item.label = item.Data?.find(
                    (z: any) => z.locale === this.translationService.currentLang
                  )?.questionnaire?.Name;
                  item.rangeLabel = `${moment(item.StartDate).format("DD/MM/YYYY")} - ${moment(item.EndDate).format(
                    "DD/MM/YYYY"
                  )}`;
                }
                break;
              case "Goal":
                isActive = !isFinishedStatus && !isExpired;
                if (isActive || allowInactive) {
                  item.show = true;
                  item.label = item.Goal;
                  item.rangeLabel = `${moment(item.StartDate).format("DD/MM/YYYY")} - ${moment(item.EndDate).format(
                    "DD/MM/YYYY"
                  )}`;
                  item.patientNote = item.Notes.filter((x: any) =>
                    x.authorReference.reference.includes("Patient")
                  )?.[0]?.text;
                  item.progress = item.Notes.filter((x: any) =>
                    x.authorReference.reference.includes("Practitioner")
                  )?.[0]?.text;
                }
                break;
            }

            if (item.show) {
              item.isInactive = allowInactive && !isActive;
              item.assignedByLabel =
                this.getAssignedByName(item.DoctorName) ||
                this.getAssignedByName(item.Doctor) ||
                this.getAssignedByName(item.DoctorFullName);
              hasVisibleItems = true;
            }
          }
        }

        type.values.sort((a: any, b: any) => {
          const aInactive = a.isInactive ? 1 : 0;
          const bInactive = b.isInactive ? 1 : 0;
          if (aInactive !== bInactive) {
            return aInactive - bInactive;
          }
          return this.getSortTimestamp(b) - this.getSortTimestamp(a);
        });

        type.showCategory = hasVisibleItems;
        if (hasVisibleItems) {
          hasAnyVisibleCategories = true;
        }
      }

      this.showCarePlan = hasAnyVisibleCategories;
    }

    this.exportToPdf();
  }

  exportToPdf() {
    if (this.patientInfo && this.patientInfo.DateOfBirth) {
      this.patientInfo.DateOfBirthLabel = moment(this.patientInfo.DateOfBirth, "YYYY-MM-DD").format("DD/MM/YYYY");
    }
    if (!this.pdf) {
      setTimeout(() => {
        this.exportToPdf();
      }, 10);
      return;
    }
    const doc = new jsPDF();
    const leftMargin = 10;
    const topMargin = 10;
    const usableWidth = doc.internal.pageSize.getWidth() - leftMargin * 2;
    const usableHeight = doc.internal.pageSize.getHeight() - topMargin - 10;

    const content = this.pdf.nativeElement;
    const sections = Array.from(content.querySelectorAll(".pdf-section") as NodeListOf<HTMLElement>).filter((section) => {
      const rect = section.getBoundingClientRect();
      return rect.height > 1;
    });
    let currentPageHeight = topMargin;

    const ensureSpace = (height: number) => {
      if (currentPageHeight + height > doc.internal.pageSize.getHeight() - 10) {
        doc.addPage();
        currentPageHeight = topMargin;
      }
    };

    const addCanvasToPdf = (canvas: HTMLCanvasElement) => {
      const ratio = canvas.height / canvas.width;
      const scaledHeight = usableWidth * ratio;

      if (scaledHeight <= usableHeight) {
        ensureSpace(scaledHeight);
        doc.addImage(canvas.toDataURL("image/png"), "PNG", leftMargin, currentPageHeight, usableWidth, scaledHeight);
        currentPageHeight += scaledHeight;
        return;
      }

      const chunkHeightPx = Math.floor((usableHeight / usableWidth) * canvas.width);
      let offsetPx = 0;

      while (offsetPx < canvas.height) {
        const sliceHeightPx = Math.min(chunkHeightPx, canvas.height - offsetPx);
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeightPx;
        const ctx = sliceCanvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(canvas, 0, offsetPx, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);
        }
        const sliceRatio = sliceHeightPx / canvas.width;
        const sliceHeight = usableWidth * sliceRatio;
        ensureSpace(sliceHeight);
        doc.addImage(
          sliceCanvas.toDataURL("image/png"),
          "PNG",
          leftMargin,
          currentPageHeight,
          usableWidth,
          sliceHeight
        );
        currentPageHeight += sliceHeight;
        offsetPx += sliceHeightPx;
      }
    };

    const addSectionToPdf = async (sectionIndex: number) => {
      if (sectionIndex >= sections.length) {
        doc.save(`${this.translationService.instant("patientReport")}.pdf`);
        this.created.emit();
        return;
      }

      const section = sections[sectionIndex] as HTMLElement;
      const canvas = await html2canvas(section, { scrollY: -window.scrollY });
      addCanvasToPdf(canvas);

      addSectionToPdf(sectionIndex + 1);
    };

    addSectionToPdf(0);
  }

  private getQuestionnaireScore(selectedAnswer: any) {
    let i = 0;
    let range = [];
    selectedAnswer.selectedAnswerScore = 0;
    const l = (selectedAnswer as any).data.Questions.length;
    for (i; i < l; i++) {
      const question = (selectedAnswer as any).data.Questions[i];
      let j = 0;
      if (question.QuestionOptions) {
        const m = question.QuestionOptions.length;
        for (j; j < m; j++) {
          const option = question.QuestionOptions[j];
          if (option.selected || option.value === true) {
            selectedAnswer.selectedAnswerScore += option.Points;
          }
        }
      }
      if (question.QuestionType === 2) {
        range.push(question.rangeValue);
      }
    }
    let sum = range.reduce((partial_sum: any, a) => partial_sum + a, 0);
    selectedAnswer.selectedAnswerScore += sum;
    selectedAnswer.selectedAnswerTotal = this.findTotalScore(selectedAnswer);
  }

  private findTotalScore(selectedAnswer: any) {
    const score = [];
    let maxPoints = 0;
    const range = [];
    for (let index = 0; index < (selectedAnswer as any).data.Questions.length; index++) {
      const element = (selectedAnswer as any).data.Questions[index];
      if (element.QuestionType === 0) {
        score.push(element.QuestionOptions);
      }
      if (element.QuestionType === 2) {
        range.push(element.QuestionMax);
      }
    }

    let sum = range.reduce((partial_sum: any, a) => partial_sum + a, 0);

    score.forEach((element) => {
      const a = element!.reduce((acc: any, shot: any) => (acc = acc > shot.Points ? acc : shot.Points), 0);
      maxPoints += a;
    });

    return maxPoints + sum;
  }

  private getAssignedByName(name: any): string | null {
    if (!name) {
      return null;
    }
    if (Array.isArray(name)) {
      for (const entry of name) {
        const formatted = this.getAssignedByName(entry);
        if (formatted) {
          return formatted;
        }
      }
      return null;
    }
    if (typeof name === "string") {
      return name;
    }
    if (name.text) {
      return name.text;
    }
    const given = Array.isArray(name.given) ? name.given.filter(Boolean).join(" ") : name.given || "";
    const family = name.family || "";
    const formatted = `${given} ${family}`.trim();
    return formatted || null;
  }

  private isInactiveStatus(status?: string) {
    if (!status) {
      return false;
    }
    const normalized = status.toString().toLowerCase();
    return ["finished", "completed", "expired", "achieved"].includes(normalized);
  }

  private hasEndDateExpired(endDate?: string | Date) {
    if (!endDate) {
      return false;
    }
    return moment(endDate).isBefore(moment(), "day");
  }

  private getSortTimestamp(item: any) {
    if (item?.CreatedAt) {
      return moment(item.CreatedAt).valueOf();
    }
    if (item?.StartDate) {
      return moment(item.StartDate).valueOf();
    }
    return 0;
  }
}
